import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, OnInit, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SseService } from '@ortho-next/sse';
import { map } from 'rxjs';
import { PAGE } from '../app.routes';
import { ToastService } from '../core';
import { PlanHeaderComponent } from '../plan';
import { PublishService } from '../services';
import { BaseComponent, ConfirmationComponent } from '../shared';
import { PublishPatientComponent } from './publish-patient';
import { PublishPlanComponent } from './publish-plan';


/**
 * Conponent for treatment publishing page.
 */
@Component({
	selector: 'tsp-publish',
	imports: [
		RouterLink,
		RouterLinkActive,
		PlanHeaderComponent,
		PublishPlanComponent,
		PublishPatientComponent
	],
	templateUrl: './publish.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PublishComponent extends BaseComponent implements OnInit, OnDestroy {

	private readonly router = inject(Router);
	private readonly location = inject(Location);
	private readonly modalSrv = inject(NgbModal);
	private readonly sseSrv = inject(SseService);
	private readonly pubSrv = inject(PublishService);
	private readonly toastSrv = inject(ToastService);

	planGuid = input<string | undefined>();

	plan = this.pubSrv.plan;
	isNextEnabled = computed(() => this.pubSrv.isPlanValid());
	isPublishEnabled = computed(() => this.pubSrv.isPlanValid() && this.pubSrv.isPatientValid());

	private _resetKey: string | null = null;

	readonly PAGE = PAGE;

	/**
	 * Check if a confirm modal is already opened.
	 */
	private readonly _isConfirmModalOpen = toSignal(this.modalSrv.activeInstances.pipe(
		map(list => list?.some(l => l.componentInstance instanceof ConfirmationComponent))
	), { initialValue: false });



	ngOnInit(): void {
		if (this.planGuid()) {
			this._handleTSPResetting();
			if (this.isPatientTab) {
				this.prev();
			}
			this.pubSrv.init(this.planGuid()!).subscribe({
				error: (err: Error) => this._handleInitError(err)
			});
		} else {
			throw new Error('Plan GUID is required');
		}
	}

	private _handleInitError(err: Error): void {
		if (err.message === 'Plan not valid') {
			this._openWarningModal(this.labels().PUBLISH_COMPONENT_INVALID_PLAN_MODAL_MESSAGE, true);
		} else {
			throw err;
		}
	}

	private readonly _handlePublishFeedback = effect(() => {
		if (this.pubSrv.publishStatus() === true) {
			this.toastSrv.showSuccess(this.labels().PUBLISH_COMPONENT_PUBLISH_SUCCESS);
			this.pubSrv.goToTlhex(this.planGuid()!);
		} else if (this.pubSrv.publishStatus() === false) {
			this.toastSrv.showError(this.labels().PUBLISH_COMPONENT_PUBLISH_ERROR);
		}
	});

	/**
	 * Check if is patient settings tab.
	 */
	get isPatientTab(): boolean {
		return this.location.path().startsWith(PAGE.PUBLISH_PATIENT);
	};

	/**
	 * Back to tlhex site.
	 */
	cancel(): void {
		this.pubSrv.goToTlhex(this.planGuid()!);
	}

	/**
	 * Back to previous tab.
	 */
	prev(): void {
		this.router.navigate([PAGE.PUBLISH_PLAN], { queryParams: { planGuid: this.planGuid() } });
	}

	/**
	 * Navigate to next tab.
	 */
	next(): void {
		if (this.isNextEnabled()) {
			this.router.navigate([PAGE.PUBLISH_PATIENT], { queryParams: { planGuid: this.planGuid() } });
		}
	}

	/**
	 * Publish treatment plan.
	 */
	publish(): void {
		if (this.isPublishEnabled()) {
			this.pubSrv.publish();
		}
	}

	private _handleTSPResetting(): void {
		if (!this._resetKey) {
			this._resetKey = `OrthoNext.TSP.Reset.${this.planGuid()}`;
			this.sseSrv.subscribe(this._resetKey, (success: boolean) => {
				if (success) {
					this._openWarningModal(this.labels().PUBLISH_COMPONENT_CHANGED_PLAN_MODAL_MESSAGE, true);
					this._clearTSPResetting();
				}
			});
		}
	}

	private _clearTSPResetting(): void {
		if (this._resetKey) {
			this.sseSrv.unsubscribe(this._resetKey);
			this._resetKey = null;
		}
	}

	private _openWarningModal(message: string, redirect: boolean): void {
		untracked(() => {
			if (!this._isConfirmModalOpen()) {
				const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
				(modalRef.componentInstance as ConfirmationComponent).config = {
					title: this.labels().PUBLISH_COMPONENT_WARNING_MODAL_TITLE,
					message: message,
					confirmBtn: this.labels().PUBLISH_COMPONENT_WARNING_MODAL_CONFIRM_BTN,
					isCancelBtn: false
				};
				modalRef.result.then(() => redirect && this.pubSrv.goToPlanList()).catch(() => false);
			}
		});
	}


	ngOnDestroy(): void {
		this.pubSrv.reset();
		this._clearTSPResetting();
	}

}
